<template>
  <div class="faq-section">
    <WContainer fluid class="px-0 py-4 bg-salmon-500 text-blue-500">
      <WRow class="mx-0">
        <WCol class="d-none d-lg-block" v-if="showIcons">
          <WImage :src="questionIcon" fluid class="text-center mt-5" />
          <WImage :src="ellipse" fluid class="text-center ms-4 mt-1" />
          <WImage :src="arrowIcon" fluid class="text-center mt-5" />
        </WCol>
        <WCol cols="12" sm="12" :lg="faqCols">
          <h2 class="text-center font-weight-bold text-blue-500 mt-2">{{ props.title }}</h2>
          <div v-if="props.extendable && !extended">
            <p class="text-center mt-4 d-none d-md-block">{{ $t('faq.extendInfo') }}</p>
            <WRow class="justify-content-center mx-0">
              <WCol cols="8" lg="3">
                <WButton
                  @click="extend"
                  outline
                  class="px-4 rounded-xl font-weight-bold line-height-lg btn-lg btn-block btn-border-white"
                >
                  {{ $t('faq.extend') }}
                </WButton>
              </WCol>
            </WRow>
          </div>
          <WFaq
            v-if="extended || !props.extendable"
            class="faq-section__faq"
            :accordion="accordion"
            :description="props.description"
            border-color="transparent"
          />
        </WCol>
        <WCol class="d-none d-lg-block" v-if="showIcons">
          <WImage :src="arrow2Icon" fluid class="text-center mt-5" />
        </WCol>
      </WRow>
      <WRow v-if="extended || !props.extendable" class="justify-content-center mx-0">
        <WCol cols="10" lg="3">
          <WButton
            :href="Links?.academyFaq"
            class="px-4 rounded-xl font-weight-bold line-height-lg btn-lg btn-block btn-border-white"
            :outline="true"
          >
            {{ $t('home.bottomSection.buttonLabel') }}
          </WButton>
        </WCol>
      </WRow>
    </WContainer>
  </div>
</template>

<script setup lang="ts">
import Links from '@/constants/links'
import type { AccordionItem } from '@wiosna-dev/wiosna-ui/types'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const { rt } = useI18n()

interface Props {
  title?: string
  description?: string
  content?: AccordionItem[]
  extendable?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  title: () => useI18n().t('faq.title'),
  content: () => []
})

const extended = ref<boolean>(false)

const extend = () => {
  extended.value = true
}

const accordion = {
  id: 'WAccordion',
  borderColor: 'transparent',
  items: props.content.map((item) => ({
    header: rt(item.header),
    text: rt(item.text)
  }))
}

const NO_CONTENT_COLS = 12
const CONTENT_COLS = 8

const showIcons = computed(() => props.content.length && (!props.extendable || extended.value))

const faqCols = computed(() => (showIcons.value ? CONTENT_COLS : NO_CONTENT_COLS))

const arrowIcon = new URL('/src/assets/svg/arrowIcon.svg', import.meta.url).href
const questionIcon = new URL('/src/assets/svg/questionIcon.svg', import.meta.url).href
const ellipse = new URL('/src/assets/svg/ellipse.svg', import.meta.url).href
const arrow2Icon = new URL('/src/assets/svg/arrow2Icon.svg', import.meta.url).href
</script>
<style lang="scss">
.faq-section {
  &__faq {
    padding: var(--spacer-4) 0;

    div.accordion-item {
      margin-bottom: var(--spacer-2);
      border: 1px solid transparent;
      border-radius: 15px;
      transition: border 0.2s ease-in-out;

      &:not(:first-of-type) {
        border-top: 1px solid transparent;
      }

      &:hover,
      &:has(div.show) {
        border: 1px solid var(--bs-white);
      }
    }
  }
}
</style>
